// Admin: Per-language hotel tagline & description editor
import React, { useEffect, useState } from 'react';
import HoursEditor from './HoursEditor';
import { AttractionsEditor } from './AttractionsEditor';
import { SeoHealthCard } from './SeoHealthCard';
import { GbpPostsCard } from './GbpPostsCard';
import { Languages, Save, Loader2, Check, AlertCircle } from 'lucide-react';
import { apiCall } from './_shared';
import { HeroPreview } from './HeroPreview';
import { CopyCheckButton, CopyCheckPanel, useCopyCheck } from './CopyCheck';

const LANGUAGES = [
  { code: 'en', name: 'English', flag: 'GB' },
  { code: 'me', name: 'Crnogorski', flag: 'ME' },
  { code: 'sq', name: 'Shqip', flag: 'AL' },
  { code: 'de', name: 'Deutsch', flag: 'DE' },
  { code: 'ru', name: 'Русский', flag: 'RU' },
  { code: 'pl', name: 'Polski', flag: 'PL' },
  { code: 'hu', name: 'Magyar', flag: 'HU' }
];

const TranslationsManager = ({ onOpenTab }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [activeLang, setActiveLang] = useState('en');
  const [taglines, setTaglines] = useState({});
  const [descriptions, setDescriptions] = useState({});
  const [fallbackTagline, setFallbackTagline] = useState('');
  const [fallbackDescription, setFallbackDescription] = useState('');
  const [hotelMeta, setHotelMeta] = useState({ name: '', city: '', country: '' });
  const [savedSnapshot, setSavedSnapshot] = useState('');
  const snapshotOf = (t, d) => JSON.stringify([t, d]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const hotel = await apiCall('/api/hotel-info');
        const tagMap = hotel.tagline_translations || {};
        const descMap = hotel.description_translations || {};

        // Ensure every supported language has an entry (empty if missing)
        const initTag = {};
        const initDesc = {};
        LANGUAGES.forEach(l => {
          initTag[l.code] = tagMap[l.code] || '';
          initDesc[l.code] = descMap[l.code] || '';
        });
        setTaglines(initTag);
        setDescriptions(initDesc);
        setSavedSnapshot(snapshotOf(initTag, initDesc));
        setFallbackTagline(hotel.tagline || '');
        setFallbackDescription(hotel.description || '');
        setHotelMeta({ name: hotel.name || '', city: hotel.city || '', country: hotel.country || '' });
        setError(null);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const [translating, setTranslating] = useState(false);
  const check = useCopyCheck('hotel homepage hero (tagline + description)');
  const runHeroCheck = () => check.run(LANGUAGES.flatMap((l) => [
    { key: `tagline:${l.code}`, lang: l.code, kind: 'tagline', text: taglines[l.code] || (l.code === 'en' ? fallbackTagline : '') },
    { key: `description:${l.code}`, lang: l.code, kind: 'description', text: descriptions[l.code] || (l.code === 'en' ? fallbackDescription : '') },
  ]));
  const applyRewrite = (r) => {
    const [field, code] = r.key.split(':');
    if (field === 'tagline') setTaglines((p) => ({ ...p, [code]: r.rewrite }));
    else setDescriptions((p) => ({ ...p, [code]: r.rewrite }));
    setActiveLang(code);
  };
  const labelFor = (r) => {
    const [field, code] = r.key.split(':');
    const l = LANGUAGES.find((x) => x.code === code);
    return `${field === 'tagline' ? 'Tagline' : 'Description'} · ${l ? l.name : code}`;
  };
  const missingCount = LANGUAGES.filter(l => l.code !== 'en' && !(descriptions[l.code] || '').trim()).length;
  const handleAiTranslate = async () => {
    setTranslating(true);
    setError(null);
    try {
      const r = await apiCall('/api/admin/hotel-info/translations/ai', { method: 'POST' });
      setTaglines(prev => ({ ...prev, ...r.tagline_translations }));
      setDescriptions(prev => ({ ...prev, ...r.description_translations }));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      if (!r.translated.length) setError('Every language already has a description — nothing to translate.');
    } catch (e) {
      setError(e.message);
    } finally {
      setTranslating(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await apiCall('/api/admin/hotel-info/translations', {
        method: 'PUT',
        body: JSON.stringify({
          tagline_translations: taglines,
          description_translations: descriptions
        })
      });
      setSavedSnapshot(snapshotOf(taglines, descriptions));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  const currentTagline = taglines[activeLang] ?? '';
  const currentDescription = descriptions[activeLang] ?? '';
  const placeholderTagline = activeLang === 'en' ? fallbackTagline : (taglines.en || fallbackTagline);
  const placeholderDescription = activeLang === 'en' ? fallbackDescription : (descriptions.en || fallbackDescription);

  return (
    <div className="space-y-6" data-testid="admin-translations-manager">
      <SeoHealthCard onOpenTab={onOpenTab} />
      <GbpPostsCard />
      <HoursEditor />
      <AttractionsEditor />
      <div id="content-translations" className="flex flex-wrap items-center justify-between gap-3 scroll-mt-24">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Languages className="w-7 h-7 text-orange-500" /> Content Translations
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Edit the hero tagline and hotel description shown on the homepage for each language.
            Leave a field empty to automatically fall back to English — or let the AI fill the missing languages.
          </p>
        </div>
        <div className="flex items-center gap-2">
        <CopyCheckButton size="lg" busy={check.busy} onClick={runHeroCheck} testId="admin-translations-quality-button" />
        <button
          onClick={handleAiTranslate}
          disabled={saving || translating}
          className="flex items-center gap-2 border border-orange-300 text-orange-700 px-4 py-2.5 rounded-lg font-semibold hover:bg-orange-50 transition-colors disabled:opacity-60"
          data-testid="admin-translations-ai-button"
          title="GPT-5.4 translates the English tagline + description into every language that is still empty"
        >
          {translating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Languages className="w-4 h-4" />}
          {translating ? 'Translating…' : `AI translate missing${missingCount ? ` (${missingCount})` : ''}`}
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-5 py-2.5 rounded-lg font-semibold hover:from-orange-600 hover:to-orange-700 transition-all shadow disabled:opacity-60"
          data-testid="admin-translations-save-button"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? 'Saving…' : 'Save All'}
        </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm" data-testid="admin-translations-error">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}
      {saved && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm" data-testid="admin-translations-saved">
          <Check className="w-4 h-4" /> Translations saved. Changes are live on the website.
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        {/* Language tabs */}
        <div className="flex flex-wrap border-b bg-gray-50">
          {LANGUAGES.map(l => {
            const hasTagline = (taglines[l.code] || '').trim().length > 0;
            const hasDesc = (descriptions[l.code] || '').trim().length > 0;
            const isComplete = hasTagline && hasDesc;
            return (
              <button
                key={l.code}
                onClick={() => setActiveLang(l.code)}
                className={`px-4 py-3 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors ${
                  activeLang === l.code
                    ? 'border-orange-500 text-orange-600 bg-white'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
                data-testid={`admin-translations-tab-${l.code}`}
              >
                <span className="text-xs font-bold text-gray-400">{l.flag}</span>
                <span>{l.name}</span>
                {isComplete && <Check className="w-3.5 h-3.5 text-green-500" />}
              </button>
            );
          })}
        </div>

        {/* Editor */}
        <div className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Hero Tagline ({LANGUAGES.find(l => l.code === activeLang)?.name})
            </label>
            <input
              type="text"
              value={currentTagline}
              onChange={(e) => setTaglines({ ...taglines, [activeLang]: e.target.value })}
              placeholder={placeholderTagline}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              data-testid={`admin-translations-tagline-${activeLang}`}
            />
            <p className="text-xs text-gray-500 mt-1">Short one-line tagline shown under the hotel name in the hero.</p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Hotel Description ({LANGUAGES.find(l => l.code === activeLang)?.name})
            </label>
            <textarea
              value={currentDescription}
              onChange={(e) => setDescriptions({ ...descriptions, [activeLang]: e.target.value })}
              placeholder={placeholderDescription}
              rows={6}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-y"
              data-testid={`admin-translations-description-${activeLang}`}
            />
            <p className="text-xs text-gray-500 mt-1">
              Long-form description shown on the hero and used for SEO meta tags.
              {currentDescription && ` · ${currentDescription.length} characters`}
            </p>
          </div>
        </div>
      </div>

      <CopyCheckPanel check={check} labelFor={labelFor} onApply={applyRewrite} testPrefix="hero-copy-check" />

      <HeroPreview
        languages={LANGUAGES}
        taglines={taglines}
        descriptions={descriptions}
        fallbackTagline={fallbackTagline}
        fallbackDescription={fallbackDescription}
        hotel={hotelMeta}
        activeLang={activeLang}
        onSelect={setActiveLang}
        dirty={snapshotOf(taglines, descriptions) !== savedSnapshot}
      />

      <div className="text-xs text-gray-500 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <strong className="text-blue-900">Tip:</strong> Empty fields fall back to the English version (or the built-in i18n default if English is also empty). Saving updates the homepage instantly for visitors.
      </div>
    </div>
  );
};

export default TranslationsManager;
